import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { TradingContext } from '../contexts/TradingContext';
import { AuthContext } from '../contexts/AuthContext';
import { isBrokerLinked, isConnectTradeSchwabAccount } from '../utils/brokerConnection';
import BrokerNotConnectedState from '../components/shared/BrokerNotConnectedState';
import PositionsTable from '../components/PositionsTable';
import OrdersTable from '../components/OrdersTable';
import AccountEquitySnapshotChart from '../components/Performance/AccountEquitySnapshotChart';
import AccountTransferModal from '../components/Performance/AccountTransferModal';
import PortfolioPage from '../components/MobileNavigation/PortfolioPage';
import AccountValueCard from '../components/AccountValueCard/AccountValueCard';
import { useRealtimeAccountMetrics } from '../hooks/useRealtimeAccountMetrics';
import useTradingRulesMetrics from '../hooks/useTradingRulesMetrics';
import useIsMobile from '../hooks/useIsMobile';
import { DASHBOARD_ROUTES } from '../constants/dashboardRoutes';
import { TIMEFRAME_PRESETS } from '../components/Performance/PerformanceTimeframeBar';
import { readStoredPeriod, writeStoredPeriod } from '../utils/reviewPeriodStorage';
import {
  getDashboardPageShellStyle,
} from '../constants/dashboardLayout';
import DashboardPageHeader from '../components/shared/DashboardPageHeader';
import DashboardSectionTitle from '../components/shared/DashboardSectionTitle';
import SchwabPnLInfoBanner from '../components/shared/SchwabPnLInfoBanner';
import { useAccountEquitySnapshot } from '../hooks/useAccountEquitySnapshot';
import { countOpenPositions, countOrdersFilledToday } from '../services/tradestationUtils';
import useTradingLockout from '../hooks/useTradingLockout';
import LockoutActiveBanner from '../components/TradingRules/LockoutActiveBanner';
import { formatLockoutExpiresAt } from '../utils/lockoutTimeFormat';
import { BannerContext } from '../contexts/BannerContext';
import './AccountOverview.css';

function getBalanceMetrics(balance, selectedAccount) {
  if (!balance || balance.AccountID !== selectedAccount?.AccountID) {
    return { equity: null, buyingPower: null, cashBalance: null, dayPL: null, openPL: null };
  }

  const equity = parseFloat(balance.Equity);
  const buyingPower = parseFloat(balance.BuyingPower);
  const cashBalance = parseFloat(balance.CashBalance);
  const dayPL = parseFloat(balance.TodaysProfitLoss);
  const openPL =
    balance.AccountType === 'Futures'
      ? parseFloat(balance.BalanceDetail?.UnrealizedProfitLoss)
      : parseFloat(balance.BalanceDetail?.UnrealizedProfitLoss ?? balance.UnrealizedProfitLoss);

  return {
    equity: Number.isFinite(equity) ? equity : null,
    buyingPower: Number.isFinite(buyingPower) ? buyingPower : null,
    cashBalance: Number.isFinite(cashBalance) ? cashBalance : null,
    dayPL: Number.isFinite(dayPL) ? dayPL : null,
    openPL: Number.isFinite(openPL) ? openPL : null,
  };
}

function normalizeViolationRuleType(ruleType) {
  if (!ruleType) return null;
  if (ruleType === 'stop_loss_modified' || ruleType === 'profit_target_modified') return null;
  if (ruleType === 'trading_window') return 'outside_trading_window';
  if (ruleType === 'daily_loss_exceeded') return 'max_loss_per_day';
  if (ruleType === 'position_loss_exceeded') return 'max_loss_per_position';
  return ruleType;
}

function normalizeViolationsByType(violationsByType) {
  const normalized = {};
  Object.entries(violationsByType || {}).forEach(([ruleType, count]) => {
    const normalizedType = normalizeViolationRuleType(ruleType);
    if (!normalizedType) return;
    normalized[normalizedType] = (normalized[normalizedType] || 0) + count;
  });
  return normalized;
}

function countViolationsToday(violationsByType) {
  const normalized = normalizeViolationsByType(violationsByType);
  return Object.entries(normalized).reduce((sum, [ruleType, count]) => {
    // Target Daily Profit is a milestone, not a broken rule.
    if (ruleType === 'profit_target_per_day') return sum;
    return sum + count;
  }, 0);
}

/**
 * Default home page for Essentials-tier users inside DashboardShell.
 * Desktop: account value, positions, orders. Backtest lives in the sidebar Tools list for admins.
 * Mobile: tabbed Portfolio (Assets, Performance, Health, Trading Rules, Journals, Orders, Alerts, Backtest for admins).
 */
export default function AccountOverview() {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <PortfolioPage />;
  }
  return <AccountOverviewDesktop />;
}

function AccountOverviewDesktop() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { isTradingLocked, lockoutExpiresAt } = useTradingLockout();
  const { user } = useContext(AuthContext);
  const { showMessage } = useContext(BannerContext);
  const brokerLinked = isBrokerLinked(user);
  const {
    balances,
    positions,
    orders,
    selectedAccount,
    isAccountDataLoading,
    isPaperTrading,
    positionsLoading,
    ordersLoading,
    netWorth,
    netWorthLoading,
    netWorthBreakdown,
    hasLiveAccounts,
  } = useContext(TradingContext);

  const [equityTimeframe, setEquityTimeframe] = useState(() => (
    readStoredPeriod('pt:reviewPeriod:accountEquity', 'month', TIMEFRAME_PRESETS.map((preset) => preset.id))
  ));
  const [equityDateRange, setEquityDateRange] = useState(null);
  const [equityBenchmark, setEquityBenchmark] = useState('SPY');
  const [compareWithIndex, setCompareWithIndex] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const handleEquityTimeframeChange = useCallback(({ timeframe: nextTf, dateRange: nextRange }) => {
    setEquityTimeframe(nextTf);
    setEquityDateRange(nextRange);
    writeStoredPeriod('pt:reviewPeriod:accountEquity', nextTf, TIMEFRAME_PRESETS.map((preset) => preset.id));
  }, []);

  const balance = balances?.find(b => b.AccountID === selectedAccount?.AccountID) || balances?.[0];

  const chartTimeframe = equityDateRange ? 'custom' : equityTimeframe;

  const { equity: equitySnapshot, isLoading: equitySnapshotLoading } = useAccountEquitySnapshot({
    timeframe: equityTimeframe,
    dateRange: equityDateRange,
    accountId: selectedAccount?.AccountID,
    account: selectedAccount,
    paperTrading: isPaperTrading,
    benchmark: compareWithIndex ? equityBenchmark : null,
    enabled: !!selectedAccount?.AccountID,
    balance,
    balancesReady: !isAccountDataLoading && !!balance,
  });

  const stableAccount = useMemo(() => {
    if (!selectedAccount?.AccountID) return null;
    return { AccountID: selectedAccount.AccountID };
  }, [selectedAccount?.AccountID]);

  const { violationsByType, loading: rulesMetricsLoading } = useTradingRulesMetrics(stableAccount, 'today');

  const violationsTodayCount = useMemo(
    () => countViolationsToday(violationsByType),
    [violationsByType]
  );

  const balanceMetrics = getBalanceMetrics(balance, selectedAccount);
  const realtimeMetrics = useRealtimeAccountMetrics();

  const cardMetrics = useMemo(() => {
    const equity = realtimeMetrics.isReady ? realtimeMetrics.accountValue : balanceMetrics.equity;
    const dayPL = realtimeMetrics.isReady
      ? (realtimeMetrics.isDirectTradestationFutures ? realtimeMetrics.todayChange : realtimeMetrics.todayUnrealizedChange)
      : balanceMetrics.dayPL;
    const openPL = realtimeMetrics.isReady ? realtimeMetrics.unrealizedPL : balanceMetrics.openPL;
    const realizedPL = realtimeMetrics.isReady ? realtimeMetrics.realizedPL : parseFloat(balance?.BalanceDetail?.RealizedProfitLoss);
    const buyingPower = realtimeMetrics.isReady ? realtimeMetrics.buyingPower : balanceMetrics.buyingPower;
    const cash = realtimeMetrics.isReady ? realtimeMetrics.cashBalance : balanceMetrics.cashBalance;
    const marketValue = realtimeMetrics.isReady ? realtimeMetrics.marketValue : null;

    return {
      equity: Number.isFinite(equity) ? equity : null,
      dayPL: Number.isFinite(dayPL) ? dayPL : null,
      openPL: Number.isFinite(openPL) ? openPL : null,
      realizedPL: Number.isFinite(realizedPL) ? realizedPL : null,
      buyingPower: Number.isFinite(buyingPower) ? buyingPower : null,
      cash: Number.isFinite(cash) ? cash : null,
      marketValue: Number.isFinite(marketValue) ? marketValue : null,
    };
  }, [realtimeMetrics, balanceMetrics, balance]);

  const openPositionsCount = useMemo(() => countOpenPositions(positions), [positions]);
  const ordersFilledTodayCount = useMemo(() => countOrdersFilledToday(orders), [orders]);
  const statsLoading = positionsLoading || ordersLoading;

  const lockoutEndsText = useMemo(() => {
    if (!lockoutExpiresAt) return null;
    return formatLockoutExpiresAt(lockoutExpiresAt);
  }, [lockoutExpiresAt]);

  return (
    <div
      style={{
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 0,
        backgroundColor: 'transparent',
        overflow: 'visible',
      }}
    >
      <div
        style={{
          ...getDashboardPageShellStyle({ isMobile: false }),
          overflowY: 'visible',
        }}
      >

      <DashboardPageHeader
        title="Portfolio"
        description="Live account value, open positions, and orders"
      />

      {isConnectTradeSchwabAccount(selectedAccount) && <SchwabPnLInfoBanner />}

      {isTradingLocked && (
        <LockoutActiveBanner endsAtText={lockoutEndsText} />
      )}

      <div
        className="account-overview-cards-grid"
      >
        <div
          className="account-overview-cards-grid__chart"
        >
          {brokerLinked ? (
            <AccountEquitySnapshotChart
              equityData={equitySnapshot}
              timeframe={chartTimeframe}
              dateRange={equityDateRange}
              onTimeframeChange={handleEquityTimeframeChange}
              colors={colors}
              isMobile={false}
              benchmark={equityBenchmark}
              onBenchmarkChange={setEquityBenchmark}
              compareWithIndex={compareWithIndex}
              onCompareWithIndexChange={setCompareWithIndex}
              isLoading={equitySnapshotLoading}
              embedded
              onAddTransfer={() => setShowTransferModal(true)}
              todayPnLPercent={
                cardMetrics.equity > 0 && cardMetrics.dayPL != null
                  ? (cardMetrics.dayPL / cardMetrics.equity) * 100
                  : null
              }
              todayPnL={cardMetrics.dayPL}
              todayValue={balanceMetrics.equity}
            />
          ) : (
            <BrokerNotConnectedState
              message="Connect your broker to track your equity curve and account performance."
              minHeight={300}
            />
          )}
        </div>

        <div
          className="account-overview-cards-grid__value"
        >
          <AccountValueCard
            equity={cardMetrics.equity}
            dayPL={cardMetrics.dayPL}
            openPL={cardMetrics.openPL}
            realizedPL={cardMetrics.realizedPL}
            buyingPower={cardMetrics.buyingPower}
            cash={cardMetrics.cash}
            marketValue={cardMetrics.marketValue}
            netWorth={netWorth}
            netWorthLoading={netWorthLoading}
            netWorthBreakdown={netWorthBreakdown}
            showNetWorth={hasLiveAccounts}
            openPositionsCount={openPositionsCount}
            ordersFilledTodayCount={ordersFilledTodayCount}
            violationsTodayCount={violationsTodayCount}
            statsLoading={statsLoading}
            violationsLoading={rulesMetricsLoading && !violationsByType}
            loading={isAccountDataLoading}
            isMobile={false}
            variant="default"
            showPrivacyToggle={false}
            showBottomStats
            defaultBottomExpanded
            fillHeight
            onViewAccountHealth={
              !rulesMetricsLoading ? () => navigate(DASHBOARD_ROUTES.health) : undefined
            }
          />
        </div>
      </div>

      <section style={{ marginBottom: 32 }}>
        <DashboardSectionTitle title="Open Positions" count={positions?.length > 0 ? positions.length : null} />
        {brokerLinked ? (
          <PositionsTable renderAsChild embedded enableLayoutControls />
        ) : (
          <BrokerNotConnectedState
            message="Connect your broker to see your open positions."
            minHeight={160}
          />
        )}
      </section>

      <section>
        <DashboardSectionTitle title="Orders" />
        {brokerLinked ? (
          <div className="account-overview-table-shell">
            <OrdersTable renderAsChild embedded paginated />
          </div>
        ) : (
          <BrokerNotConnectedState
            message="Connect your broker to see your orders."
            minHeight={160}
          />
        )}
      </section>
      </div>
      <AccountTransferModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        accountId={selectedAccount?.AccountID}
        isPaperTrading={selectedAccount?.isPaperTrading ?? isPaperTrading}
        showMessage={showMessage}
      />
    </div>
  );
}
